import { compose } from './compose.no-deps'
import { identity } from './function.no-deps'

/**
 * Partial application of `promise.then` to be used in `pipe`.
 * Multiple arguments function as a pipe to emulate a block.
 *
 *    await pipe(
 *        Promise.resolve(range(100, 250)),
 *        promiseThen(
 *            map((n, i) => n / i),
 *            reduce(0, sum),
 *            n => (n > 50 ? 'Woow' : 'Oh, really?')
 *        )
 *    )
 */
export function promiseThen<T1, T2>(f1: (t: T1) => T2 | Promise<T2>): (promise: Promise<T1>) => Promise<T2>

export function promiseThen<T1, T2, T3>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3 | Promise<T3>
): (promise: Promise<T1>) => Promise<T3>

export function promiseThen<T1, T2, T3, T4>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4 | Promise<T4>
): (promise: Promise<T1>) => Promise<T4>

export function promiseThen<T1, T2, T3, T4, T5>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5 | Promise<T5>
): (promise: Promise<T1>) => Promise<T5>

export function promiseThen<T1, T2, T3, T4, T5, T6>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6 | Promise<T6>
): (promise: Promise<T1>) => Promise<T6>

export function promiseThen<T1, T2, T3, T4, T5, T6, T7>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7 | Promise<T7>
): (promise: Promise<T1>) => Promise<T7>

export function promiseThen<T1, T2, T3, T4, T5, T6, T7, T8>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8 | Promise<T8>
): (promise: Promise<T1>) => Promise<T8>

export function promiseThen<T1, T2, T3, T4, T5, T6, T7, T8, T9>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9 | Promise<T9>
): (promise: Promise<T1>) => Promise<T9>

export function promiseThen<T1, T2, T3, T4, T5, T6, T7, T8, T9, T10>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9,
    f9: (t: T9) => T10 | Promise<T10>
): (promise: Promise<T1>) => Promise<T10>

export function promiseThen<T1, T2, T3, T4, T5, T6, T7, T8, T9, T10, T11>(
    f1: (t: T1) => T2,
    f2: (t: T2) => T3,
    f3: (t: T3) => T4,
    f4: (t: T4) => T5,
    f5: (t: T5) => T6,
    f6: (t: T6) => T7,
    f7: (t: T7) => T8,
    f8: (t: T8) => T9,
    f9: (t: T9) => T10,
    f10: (t: T10) => T11 | Promise<T11>
): (promise: Promise<T1>) => Promise<T11>

export function promiseThen(
    f1: (t: any) => any = identity,
    f2: (t: any) => any = identity,
    f3: (t: any) => any = identity,
    f4: (t: any) => any = identity,
    f5: (t: any) => any = identity,
    f6: (t: any) => any = identity,
    f7: (t: any) => any = identity,
    f8: (t: any) => any = identity,
    f9: (t: any) => any = identity,
    f10: (t: any) => any | Promise<any> = identity
): (promise: Promise<any>) => Promise<any> {
    const fn = compose(f1, f2, f3, f4, f5, f6, f7, f8, f9, f10)
    return (promise: Promise<any>) => promise.then(fn)
}
